import { useState, useCallback } from 'react';

export type ToastType = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

let _nextId = 0;

export function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const show = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++_nextId;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts, show, dismiss };
}

const STYLES: Record<ToastType, { bg: string; border: string; iconColor: string; icon: string }> = {
  success: { bg: '#d8f1e3', border: '#a7e8c0', iconColor: '#086a3f', icon: '✓' },
  error: { bg: '#fde2e5', border: '#f4b5c1', iconColor: '#a02835', icon: '✕' },
  info: { bg: '#ece9ff', border: '#c7c3f5', iconColor: '#4a3fe0', icon: 'ℹ' },
};

export function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  if (toasts.length === 0) return null;
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 80,
        right: 16,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: 'min(340px, calc(100vw - 32px))',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => {
        const s = STYLES[t.type];
        return (
          <div
            key={t.id}
            style={{
              background: s.bg,
              border: `1px solid ${s.border}`,
              borderRadius: 10,
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              backdropFilter: 'blur(12px)',
              boxShadow: '0 8px 24px rgba(10,18,48,.12)',
              pointerEvents: 'auto',
              fontSize: '.82rem',
              color: '#0a1230',
              lineHeight: 1.45,
            }}
          >
            <span
              style={{
                color: s.iconColor,
                fontWeight: 700,
                flexShrink: 0,
                fontSize: '.85rem',
                marginTop: 1,
              }}
            >
              {s.icon}
            </span>
            <span style={{ flex: 1 }}>{t.message}</span>
            <button
              onClick={() => onDismiss(t.id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#9aa0bc',
                fontSize: '1rem',
                padding: 0,
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}
